import { type MapRef } from "@vis.gl/react-maplibre";
import { FC, useMemo, useRef, useState } from "react";
import { useRecoilValue } from "recoil";

import {
  IRideDto,
  isLiveMapReducedDisplayMode,
  isRenderPolylineAllowedAtom,
  isUiStagingDomain,
  MapRouteBlockTypeEnum,
  mapViewModeAF,
  MapViewModeEnum,
  mergeArrays,
  pendingRideFetchAtom,
  ProfileStorage,
  useDebugMode,
} from "@ryde/shared";

import { MapToolbox } from "../map-toolbox";
import { IRouteBlockProps } from "../route-block.models";
import { MapLibreMissingStyleImages } from "./parts/maplibre-missing-style-images";
import { MapLibreRouteBlockContent } from "./parts/maplibre-route-block-content";
import { StyledRouteBlockMapLibreHost } from "./parts/route-block.maplibre.styled";
import { useMapLibreRidesAutoFit } from "./parts/use-maplibre-rides-auto-fit";
import { CROWDED_MAP_MIN_ROUTES } from "./route-block-maplibre.constants";

export const RouteBlockMapLibre: FC<IRouteBlockProps> = ({
  routes,
  trackingCarRoutes,
  hiddenMapState,
  rideDto,
  routeBlockType,
  isMapCoveredByRightDrawer,
  mapExtraLayers,
  liveMapDisplayMode,
}) => {
  const mapRef = useRef<MapRef>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const viewMode = useRecoilValue(mapViewModeAF(routeBlockType));
  const isLiveMapReduced =
    !!liveMapDisplayMode && isLiveMapReducedDisplayMode(liveMapDisplayMode);
  const isHybridMode = ProfileStorage.isHybridMode;
  const isRideFetchPending = useRecoilValue(pendingRideFetchAtom);
  const isRenderPolylineAllowed = useRecoilValue(isRenderPolylineAllowedAtom);
  const isUiStaging = isUiStagingDomain();
  const { isRideCardMapToolboxDebugMode } = useDebugMode();

  const showMapToolboxCondition = useMemo(() => {
    return (
      routeBlockType === MapRouteBlockTypeEnum.RideCard &&
      !isHybridMode &&
      !isUiStaging &&
      isRideCardMapToolboxDebugMode
    );
  }, [
    isRideCardMapToolboxDebugMode,
    isHybridMode,
    isUiStaging,
    routeBlockType,
  ]);

  const autofitRoutes = useMemo(() => {
    if (viewMode === MapViewModeEnum.TrackingsOnly) {
      return trackingCarRoutes || [];
    }

    if (viewMode === MapViewModeEnum.RouteOnly) {
      return routes || [];
    }

    return mergeArrays(routes, trackingCarRoutes);
  }, [routes, trackingCarRoutes, viewMode]);

  useMapLibreRidesAutoFit({
    mapRef,
    routes: autofitRoutes,
    routeBlockType,
    isMapLoaded,
    isMapHidden: hiddenMapState,
  });

  // Past this many routes the per-route costs stop being affordable: stations are the biggest DOM cost
  // (~48 stops per ride) and the GPS trail's arrows and dots the biggest per-poll cost. Single-route
  // screens — ride card, ride edit, the editors — and every everyday multi-ride selection stay below it and
  // keep the numbered pins. The planned route's direction arrows are never traded away, at any size.
  const isCrowdedMap = (routes?.length ?? 0) >= CROWDED_MAP_MIN_ROUTES;

  const isMapCoveredByDrawerUpdating = useMemo(() => {
    return Boolean(isMapCoveredByRightDrawer && isRideFetchPending);
  }, [isMapCoveredByRightDrawer, isRideFetchPending]);

  const isRouteBlockShownCondition = useMemo(() => {
    if (!routes?.length || isMapCoveredByDrawerUpdating) {
      return false;
    }

    if (isLiveMapReduced) {
      return true;
    }

    return Boolean(
      viewMode !== MapViewModeEnum.TrackingsOnly && isRenderPolylineAllowed,
    );
  }, [
    isLiveMapReduced,
    isMapCoveredByDrawerUpdating,
    routes,
    viewMode,
    isRenderPolylineAllowed,
  ]);

  const isTrackingRouteBlockShownCondition = useMemo(() => {
    if (!trackingCarRoutes?.length || isMapCoveredByDrawerUpdating) {
      return false;
    }

    if (isLiveMapReduced) {
      return true;
    }

    return Boolean(
      viewMode !== MapViewModeEnum.RouteOnly && isRenderPolylineAllowed,
    );
  }, [
    isLiveMapReduced,
    isMapCoveredByDrawerUpdating,
    trackingCarRoutes?.length,
    viewMode,
    isRenderPolylineAllowed,
  ]);

  return (
    <StyledRouteBlockMapLibreHost
      mapRef={mapRef}
      isHidden={hiddenMapState}
      isFullscreenEnabled
      onLoadedChange={setIsMapLoaded}
      header={
        showMapToolboxCondition &&
        Boolean(rideDto) && (
          <MapToolbox
            routes={routes}
            trackingCarRoutes={trackingCarRoutes}
            rideDto={rideDto as IRideDto}
            routeBlockType={routeBlockType}
          />
        )
      }
    >
      {/* First child on purpose: it installs the runtime-image resolver in a layout effect, which has
          to happen before any sibling symbol layer asks v6 for an icon. */}
      <MapLibreMissingStyleImages />

      {isMapLoaded && isRouteBlockShownCondition && (
        <MapLibreRouteBlockContent
          routes={routes}
          direction={rideDto?.direction}
          liveMapDisplayMode={liveMapDisplayMode}
          isCrowdedMap={isCrowdedMap}
        />
      )}

      {isMapLoaded && isTrackingRouteBlockShownCondition && (
        <MapLibreRouteBlockContent
          routes={trackingCarRoutes}
          direction={rideDto?.direction}
          liveMapDisplayMode={liveMapDisplayMode}
          isCrowdedMap={isCrowdedMap}
        />
      )}

      {/* Inside the zoom provider too, so an extra layer that reads the zoom gets the real one.
          Pins wait until the map has loaded — adding them earlier crashes the ride card. */}
      {isMapLoaded && mapExtraLayers}
    </StyledRouteBlockMapLibreHost>
  );
};
